<template>
  <div id="cryptocurrency-app">
    <main v-if="marketInfo" class="main crypto-currency-page">
      <HeaderInner class="crypto-currency-header" v-once>
        <template v-slot:breadcrumbs v-if="isPublicPage">
          <a :href="urlTo('/')">{{ $t('prices.home') }}</a>
          <a :href="urlTo('/price/')">{{ $t('prices.title') }}</a>
          <span>{{ selectedCryptoCurrency.name }}</span>
        </template>
        <template v-slot:heading>
          <CryptoIcon
            class="crypto-currency-header__icon"
            :icon-name="selectedCryptoCurrency.code.toLowerCase()"
          />
          <span
            class="crypto-currency-header__heading"
          >{{ $t('prices.cryptoCurrency.title', { crypto: selectedCryptoCurrency.name }) }}</span>
        </template>
      </HeaderInner>

      <div class="container main-crypto-block">
        <div class="chart-container">
          <div class="chart-container__filters">
            <CustomSelect
                class="mr-16"
                @updated="changeFiatCurrency"
                :values="fiatCurrencyList"
                :selectedItem="selectedFiat"
            />
            <CustomSelect
                class="ml-16 mr-16"
                @updated="requestData(selectedMarket, selectedFiatCurrency, $event)"
                :values="constants.TimeIntervals"
                :selectedItem="selectedTimeInterval"
            />
          </div>
          <MainChart
            :crypto-currency="selectedCryptoCurrency"
            :price="marketInfo.price"
            :change="marketInfo.chg24h"
            :chart-data="historicalData"
            :buy-link="buyLink"
            :sell-link="sellLink"
            :loading="loading"
            @buy="navigate(buyLink)"
            @sell="navigate(sellLink)"
          />
          <div class="chart-container__description">
            <div class="description-wrapper">
              <span class="title">{{ $t('prices.cryptoCurrency.marketCap') }}</span>
              <transition name="fade">
                <span :key="marketCapValue" class="value" v-html="marketCapValue" :title="marketCapValue"></span>
              </transition>
            </div>
            <div class="description-wrapper">
              <span class="title">{{ $t('prices.cryptoCurrency.volume') }}</span>
              <transition name="fade">
                <span :key="volumeValue" class="value" v-html="volumeValue" :title="volumeValue"></span>
              </transition>
            </div>
            <div class="description-wrapper">
              <span class="title" v-html="$t('prices.cryptoCurrency.allTimeHigh')"></span>
              <transition name="fade">
                <span :key="allTimeHighValue" class="value" v-html="allTimeHighValue" :title="allTimeHighValue"></span>
              </transition>
            </div>
            <div class="description-wrapper">
              <span class="title">{{ $t('prices.cryptoCurrency.circulationSupply') }}</span>
              <transition name="fade">
                <span :key="circulatingSupplyValue" class="value" v-html="circulatingSupplyValue" :title="circulatingSupplyValue"></span>
              </transition>
            </div>
          </div>

          <div class="chart-container__about" v-once>
            <div class="title">{{ $t('prices.cryptoCurrency.about_title') }}</div>
            <div class="text-block" v-html="description"></div>
          </div>
        </div>
        <div class="other-cryptos">
          <div class="other-cryptos__title">
            {{ $t('prices.otherCryptos.title') }}
          </div>
          <OtherCryptoRow
              v-for="crypto in otherCryptos"
              :key="crypto.code"
              :title="crypto.name"
              :slug="crypto.slug"
              :code="crypto.code"
              :price="crypto.price"
              :change="Number(crypto.chg24h).toFixed(2)"
          />
        </div>
      </div>

      <!-- Temporary hide this component, for SEO pages v1 -->
      <!--    <div class="container">-->
      <!--      <TradeBlock/>-->
      <!--    </div>-->
    </main>
  </div>
</template>

<script>
import UrlGenerator from '@paybis/frontend-common-lib/src/services/url';
import { mapActions,mapState } from 'vuex';

import MainChart from '@/components/CryptoCurrency/MainChart.vue';
import OtherCryptoRow from '@/components/CryptoCurrency/OtherCryptoRow.vue';
import CryptoIcon from '@/components/Prices/CryptoIcon.vue';
import CustomSelect from '@/components/Prices/CustomSelect.vue';
import HeaderInner from '@/components/Prices/HeaderInner.vue';
import fullPrice from '@/helpers/fullPrice';
import navigate from '@/helpers/navigation';
import * as constants from '@/models/pricesConstants';
import ws from '@/plugins/cryptoPriceWS';
import { useBotDetect } from '@paybis/frontend-common-lib/src/composables/useBotDetect';

export default {
  name: 'CryptoCurrencyPage',

  components: {
    HeaderInner,
    CryptoIcon,
    OtherCryptoRow,
    CustomSelect,
    MainChart,
    // TradeBlock
  },

  data () {
    return {
      constants,
      loading: false,
      navigate,
      wsConnectTimeout: -1,
    };
  },

  computed: {
    ...mapState(['language']),
    ...mapState('cryptoCurrency', ['selectedCryptoCurrency', 'selectedMarket', 'selectedFiatCurrency', 'selectedTimeInterval', 'marketInfo', 'otherCryptos', 'description', 'historicalData', 'availableFiatCurrencies', 'isPublicPage', 'isCorporateAccount', 'defaultBuyLink', 'defaultSellLink', 'wsConnected']),

    marketCapValue () {
      if (this.marketInfo) {
        return fullPrice(this.language, this.selectedFiatCurrency.code, this.marketInfo.mktcap);
      }
      return 0;
    },

    volumeValue () {
      if (this.marketInfo) {
        return fullPrice(this.language, this.selectedFiatCurrency.code, this.marketInfo.volume24h);
      }
      return 0;
    },

    allTimeHighValue () {
      if (this.marketInfo) {
        return fullPrice(this.language, this.selectedFiatCurrency.code, this.marketInfo.high24h);
      }
      return 0;
    },

    circulatingSupplyValue () {
      let circulatingSupply = 0;
      if (this.marketInfo) {
        const formatter = new Intl.NumberFormat(`${this.language}-${this.language.toUpperCase()}`);
        const amount = Number(this.marketInfo.supply);

        circulatingSupply = formatter.format(amount);
      }

      return circulatingSupply + '&nbsp;' + this.selectedCryptoCurrency.code;
    },

    buyLink () {
      if (this.isCorporateAccount) return this.defaultBuyLink;
      return this.selectedCryptoCurrency.links.buy ? this.urlToExchange(this.selectedCryptoCurrency.links.buy) : null;
    },

    sellLink () {
      if (this.isCorporateAccount) return this.defaultSellLink;
      return this.selectedCryptoCurrency.links.sell ? this.urlToExchange(this.selectedCryptoCurrency.links.sell) : null;
    },

    fiatCurrencyList () {
      return this.availableFiatCurrencies.map((fiatCurrency) => {
        return {
          value: fiatCurrency.code,
          title: fiatCurrency.code,
        };
      });
    },

    selectedFiat () {
      return {
        value: this.selectedFiatCurrency.code,
        title: this.selectedFiatCurrency.code,
      };
    },
  },

  beforeMount () {
    // Deliberately not in data(): Vue must not make a live WebSocket holder reactive.
    this.priceSocket = ws('cryptoCurrency', this.selectedMarket.value, this.selectedFiatCurrency.code);
    const { isBot } = useBotDetect();
    if (!isBot) {
      this.wsConnectTimeout = setTimeout(this.priceSocket.connect, 15_000);
    }
    if (this.historicalData.length > 0) {
      return;
    }

    this.requestHistoricData({
      market: this.selectedMarket,
      fiatCurrency: this.selectedFiatCurrency,
      timePeriod: this.selectedTimeInterval,
    });
  },

  watch: {
    wsConnected() {
      clearTimeout(this.wsConnectTimeout);
    },
  },

  methods: {
    ...mapActions({
      requestMarketInfo: 'cryptoCurrency/requestMarketInfo',
      requestHistoricData: 'cryptoCurrency/requestHistoricData',
    }),

    changeFiatCurrency (newFiat) {
      const fiatCurrency = this.availableFiatCurrencies.find((fiat) => fiat.code === newFiat.value);
      this.requestData(this.selectedMarket, fiatCurrency, this.selectedTimeInterval);
    },

    requestData (market, fiatCurrency, timePeriod) {
      this.loading = true;
      const isMarketChanged = market.value !== this.selectedMarket.value;
      const isFiatChanged = fiatCurrency.code !== this.selectedFiatCurrency.code;
      const promises = [
        this.requestHistoricData({ market, fiatCurrency, timePeriod }),
      ];

      if (isMarketChanged || isFiatChanged) {
        if (this.wsConnected) {
          this.priceSocket.disconnect();
        }
        const promise = this.requestMarketInfo({ market, fiatCurrency }).then(() => {
          this.priceSocket.connectToPrice(market.value, fiatCurrency.code);
        });
        promises.push(promise);
      }

      Promise
        .all(promises)
        .finally(() => { this.loading = false; });
    },

    urlToExchange(link) {
      const urlPrefix = this.isPublicPage ? '' : '/exchange';

      return this.urlTo(urlPrefix + link);
    },

    urlTo (link) {
      const urlPrefix = this.isPublicPage ? '' : '/member';

      return UrlGenerator.generate(urlPrefix + link);
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'scss-vars';
@use "@assets/scss/components/prices/loader";

.text-block h2 {
  font-size: 1.625rem;
  line-height: 1.23;
  @media screen and (min-width: scss-vars.$screen-lg-min) {
    font-size: 2rem;
    line-height: 1.37;
  }
}
  .crypto-currency-page {
    .container {
      &.main-crypto-block {
        display: flex;

        .chart-container {
          width: 65%;
          max-width: 65%;
          margin-right: auto;

          @include loader.loader;

          .chart-container__filters {
            display: flex;
            height: 64px;

            .nav-dropdown {
              display: flex;
              height: 45px;
            }
          }

          .chart-container__description {
            display: flex;
            justify-content: space-between;
            margin: 24px -8px 0;

            .description-wrapper {
              padding: 0 8px;
              display: flex;
              flex-direction: column;

              .title {
                font-size: 16px;
                line-height: 24px;
              }

              .value {
                font-family: scss-vars.$font-bold;
                font-size: 16px;
                line-height: 24px;
              }
            }
          }

          .chart-container__about {
            margin-top: 60px;

            .title {
              font-family: scss-vars.$font-bold;
              font-size: 22px;
              line-height: 28px;
              margin-bottom: 24px;
            }
          }

          @media screen and (max-width: scss-vars.$screen-sm-max) {
            .chart-container__description {
              flex-wrap: wrap;

              .description-wrapper {
                width: 50%;
                align-items: center;
                margin-top: 24px;
              }
            }
          }

          @media screen and (max-width: scss-vars.$screen-prices-xs-max) {
            .chart-container__description {
              flex-wrap: wrap;

              .description-wrapper {
                width: 100%;
                align-items: center;
                margin-top: 24px;
              }
            }
          }
        }

        .other-cryptos {
          max-width: 30%;
          width: 30%;

          &:first-child {
            border-top: 1px solid scss-vars.$black-06;
          }

          &__title {
            font-family: scss-vars.$font-bold;
            font-size: 18px;
            line-height: 64px;
            border-bottom: 1px solid scss-vars.$black-06;
          }
        }
      }
    }

    @media screen and (max-width: scss-vars.$screen-md-max) {
      .container {
        &.main-crypto-block {
          flex-direction: column;

          .chart-container {
            width: 100%;
            max-width: 100%;
          }

          .other-cryptos {
            width: 100%;
            max-width: 100%;
          }
        }
      }
    }
    .chart-container__description .value {
      @media screen and (min-width: scss-vars.$screen-md-min) {
        max-width: 200px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      @media screen and (min-width: scss-vars.$screen-lg-min) {
        max-width: 155px;
      }

      @media screen and (min-width: scss-vars.$screen-xlg-min) {
        max-width: 220px;
      }
    }
  }

  // @TODO code was moved to global css. Check how it will be fork without local styles.
  .fade-leave-active {
    display: none;
  }
  .fade-enter-active {
    transition: opacity 1s;
  }
  .fade-enter,
  .fade-enter-from {
    opacity: 0.1;
  }

  // Prevent whitespace between inline elements
  .crypto-currency-header {
    &__icon {
      display: flex;
      margin-right: 10px;
    }

    &__heading {
      display: block;
    }
  }
</style>

<style lang="scss">
@use 'scss-vars';

.text-block h2 {
  font-size: 1.625rem;
  line-height: 1.23;
  @media screen and (min-width: scss-vars.$screen-lg-min) {
    font-size: 2rem;
    line-height: 1.37;
  }
}
.crypto-currency-header.header-inner {
  @media screen and (min-width: scss-vars.$screen-lg-min) {
    padding: 40px 24px;
  }
}
</style>
